import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DraftPermissionRow, DraftPermissionRowMap } from "@manageContacts/models";
import { deriveNotifications } from "@manageContacts/utils/editPermissions";
import { fetchContactsThunk } from "./thunks";
import { ContactsListResponse, ManageContactsState, SavedPermissionEntry } from "./models";
import { loadEditStateFromStorage } from "./persistence";

// Hydrate the edit-mode slice from sessionStorage exactly once on module load. This
// runs before `configureStore` reads `initialState`, so a hard refresh (F5) or new
// tab restored from the browser's session restores any in-progress edits without
// blocking the page render on an async hydration step. The listener middleware
// (see `./listeners`) keeps sessionStorage in sync on subsequent edits.
const initialState: ManageContactsState = {
  contacts: {
    data: null,
    loading: false,
    error: null,
  },
  edit: loadEditStateFromStorage(),
};

type DraftField = keyof DraftPermissionRow;

const manageContactsSlice = createSlice({
  name: "manageContacts",
  initialState,
  reducers: {
    /**
     * Initialise edit mode with the baseline + initial draft snapshots produced by the
     * page on the click of "Edit". `draftRowMap` may already differ from `originalRowMap`
     * (e.g. the page pre-derived missing notifications); we trust the caller-supplied
     * payload as-is. Callers MUST pass freshly cloned maps to avoid aliasing the page's
     * source data.
     */
    enterEditMode: (
      state,
      action: PayloadAction<{ originalRowMap: DraftPermissionRowMap; draftRowMap: DraftPermissionRowMap }>
    ) => {
      state.edit.isEditMode = true;
      state.edit.originalRowMap = action.payload.originalRowMap;
      state.edit.draftRowMap = action.payload.draftRowMap;
    },

    /**
     * Apply a single field edit to a draft row and re-derive Notifications when
     * Document Access changes (auto-add matching notification on add, auto-remove on
     * unselect). When Notifications is edited directly, filter to the currently
     * allowed doc-access set so users cannot retain a notification whose document
     * access permission is no longer selected.
     */
    changeDraftField: (state, action: PayloadAction<{ rowId: string; field: DraftField; nextValues: string[] }>) => {
      const { rowId, field, nextValues } = action.payload;
      const currentDraft = state.edit.draftRowMap[rowId];

      if (!currentDraft) return;

      const nextDraft: DraftPermissionRow = {
        ...currentDraft,
        [field]: nextValues,
      };

      if (field === "documentAccess") {
        nextDraft.notifications = deriveNotifications(nextDraft, currentDraft, state.edit.originalRowMap[rowId]);
      } else if (field === "notifications") {
        const allowed = new Set(nextDraft.documentAccess);

        nextDraft.notifications = nextValues.filter((n) => allowed.has(n));
      }

      state.edit.draftRowMap[rowId] = nextDraft;
    },

    /**
     * Restore a single row in the draft back to its baseline snapshot. Used by the
     * per-row undo icon in the table.
     */
    undoRowEdit: (state, action: PayloadAction<string>) => {
      const rowId = action.payload;
      const baseline = state.edit.originalRowMap[rowId];

      if (!baseline) return;

      state.edit.draftRowMap[rowId] = {
        roles: [...baseline.roles],
        documentAccess: [...baseline.documentAccess],
        notifications: [...baseline.notifications],
      };
    },

    /**
     * Exit edit mode and clear all drafts. Used by Cancel / Discard Changes, and by
     * Save Changes after a successful POST.
     */
    exitEditMode: (state) => {
      state.edit = { isEditMode: false, originalRowMap: {}, draftRowMap: {} };
    },

    /**
     * Locally apply a set of saved permission edits to the in-memory contacts slice so
     * the table re-renders with the saved values without re-fetching. The payload is a
     * list of `SavedPermissionEntry` where `rowId` matches `Contact.id` (either
     * `${contactId}-${fundId}` for fund-scoped rows, or `${contactId}` for the no-fund row).
     *
     * Roles are stored on the API as a CSV string on `fund.role` to round-trip with the
     * GET response; documentAccess/notifications are reset to thin objects carrying only
     * `displayName` (the only field the page consumes).
     */
    mergeContactPermissions: (state, action: PayloadAction<SavedPermissionEntry[]>) => {
      if (!state.contacts.data) return;

      const byRowId = new Map(action.payload.map((entry) => [entry.rowId, entry]));

      for (const contact of state.contacts.data.contacts) {
        const account = contact.account;

        // No-fund contact rows have no fund to mutate; the GET response is the source of
        // truth for those rows, so we skip them here.
        if (!account || !account.funds || account.funds.length === 0) {
          continue;
        }

        for (const fund of account.funds) {
          const rowId = `${contact.id}-${fund.fundId}`;
          const entry = byRowId.get(rowId);

          if (!entry) continue;

          fund.role = entry.roles.join(",");
          fund.documentAccess = entry.documentAccess.map((displayName) => ({
            serviceId: 0,
            serviceName: displayName,
            serviceArea: null as unknown as string,
            displayName,
            entitlements: null,
            documentTypes: null,
          }));
          fund.notifications = entry.notifications.map((displayName) => ({
            notificationId: 0,
            notificationName: displayName,
            notificationArea: null as unknown as string,
            displayName,
          }));
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContactsThunk.pending, (state) => {
        state.contacts.loading = true;
        state.contacts.error = null;
      })
      .addCase(fetchContactsThunk.fulfilled, (state, action: PayloadAction<ContactsListResponse>) => {
        state.contacts.data = action.payload;
        state.contacts.loading = false;
        state.contacts.error = null;
      })
      .addCase(fetchContactsThunk.rejected, (state, action) => {
        state.contacts.loading = false;
        state.contacts.error = action.payload as string;
      });
  },
});

export const { enterEditMode, changeDraftField, undoRowEdit, exitEditMode, mergeContactPermissions } =
  manageContactsSlice.actions;

export default manageContactsSlice.reducer;
